import { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';
import { BiSolidError } from 'react-icons/bi';

interface FormFieldProps {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  warning?: string;
  /** Plain string in most callers; T126 widens this to `ReactNode` so a hint can carry `<bdi>`-
   * wrapped money/date content (e.g. the exam-reservation live EGP preview) while still getting
   * the automatic `aria-describedby` wiring below. */
  hint?: ReactNode;
  children: ReactNode;
}

/**
 * Shared label + input + error/warning/hint wrapper — 10-design-system.md §4.2. Clones the single
 * child input/select/textarea to wire `id`, `aria-invalid` and `aria-describedby` automatically so
 * every field in the app gets consistent, correct a11y wiring without repeating it by hand.
 *
 * Error (red, blocking) and warning (amber, non-blocking — e.g. national ID/phone format) are
 * visually and programmatically distinct per 08-staff-ux.md §5.2 "warn, don't block".
 */
export function FormField({ id, label, required, error, warning, hint, children }: FormFieldProps) {
  const describedBy =
    [
      error ? `${id}-error` : null,
      !error && warning ? `${id}-warning` : null,
      hint ? `${id}-hint` : null,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  const child = isValidElement(children) ? (children as ReactElement) : null;
  const enhanced = child
    ? cloneElement(child, {
        id,
        'aria-invalid': Boolean(error),
        'aria-describedby': describedBy,
        'aria-required': required || undefined,
        // Non-blocking warning state gets its own amber border (§4.2) — visually distinct from
        // the blocking `aria-invalid` error state, never set at the same time as an error.
        'data-warning': !error && warning ? 'true' : undefined,
      })
    : children;

  return (
    <div className="fa-field">
      <label htmlFor={id}>
        {label}
        {required && (
          <span className="fa-field__required" aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </label>
      {enhanced}
      {error && (
        <p id={`${id}-error`} className="fa-field__error">
          {error}
        </p>
      )}
      {!error && warning && (
        <p id={`${id}-warning`} className="fa-field__warning">
          <BiSolidError aria-hidden="true" /> {warning}
        </p>
      )}
      {hint && (
        <p id={`${id}-hint`} className="fa-field__hint">
          {hint}
        </p>
      )}
    </div>
  );
}
